import { distinctUntilChanged } from 'rxjs';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, Output } from '@angular/core';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type AbstractControl, ControlContainer, FormGroupDirective } from '@angular/forms';
import type { DropdownChangeEvent } from "primeng/dropdown";
import * as FlatModels from '@medsurf/flat-models';

@Component({
    selector: 'medsurf-design-form-control-input-dropdown',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './dropdown.input.form-control.design.html',
    styleUrls: ['./dropdown.input.form-control.design.scss'],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ],
    standalone: false
})
export class DropdownInputFormControlDesign implements OnInit, OnChanges {
  @Input() formField = true;
  @Input({ required: true }) control!: AbstractControl;
  @Input() dropdownClass?: string;
  @Input() inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass = FlatModels.ButtonDesignModels.ButtonDesignClass.DEFAULT;
  @Input() menuClass: FlatModels.PanelDesignModels.MenuDesignClass = FlatModels.PanelDesignModels.MenuDesignClass.DEFAULT;
  @Input() options: ({ name: string, value: string } | any)[] = [];
  @Input() optionLabel = 'name'
  @Input() optionValue = 'value'
  @Input() placeholder = "";
  @Input() label?: string;
  @Input() labelIcon?: FlatModels.ButtonDesignModels.ButtonDefaultIcons;
  @Input() filter?: boolean;
  @Input() inputGroup = false;
  @Input() disabled = false;
  @Input() autofocus = false;
  @Input() showClear = false;
  @Output() valueChange = new EventEmitter<DropdownChangeEvent>();
  
  readonly FlatModels = FlatModels;

  name?: string;

  constructor(private cd: ChangeDetectorRef, private destroyRef: DestroyRef) {
  }

  ngOnInit() {
    const formGroup = this.control.parent;
    this.name = formGroup ? Object.keys(formGroup.controls).find(name => this.control === formGroup.get(name)) : undefined;

    this.control?.statusChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(() => {
      this.cd.markForCheck();
    });

    if (this.name === undefined) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.form_control_name_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatDesign.DropdownInputFormControlDesign.ngOnInit"
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']?.currentValue) {
      this.control?.disable();
    } else {
      this.control?.enable();
    }
  }

  getLabel(value: any) {
    const option = this.options.find(o => (o as any)[this.optionValue] === value);
    if (option) {
      return (option as any)[this.optionLabel];
    }
    return value;
  }
}
